import * as yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

// State-related row data for boxes 16-18 (supports up to 2 rows)
export const stateRowSchema = yup.object().shape({
  stateTaxWithheld: yup.string().nullable().optional(),
  statePayerStateNo: yup.string().nullable().optional(),
  stateIncome: yup.string().nullable().optional(),
})

export const slMunson1099FormFileDataSchema = yup.object().shape({
  // Meta / Header
  taxYear: yup.string().nullable().optional(),
  copyType: yup.string().nullable().optional(), // "Copy A", "Copy B", "Copy 1", "Copy 2", etc.
  formVariant: yup.string().nullable().optional(), // Typically "1099-MISC"
  formRevision: yup.string().nullable().optional(), // Form revision date (e.g., "April 2025")
  corrected: yup.string().nullable().optional(), // Checkbox: "Yes" or "true" to check
  secondTinNotification: yup.string().nullable().optional(), // Checkbox: "Yes" or "true" to check "2nd TIN not."

  // Payer Information
  payer: yup.object().shape({
    name: yup.string().nullable().optional(),
    streetAddress: yup.string().nullable().optional(),
    cityStateZipCountry: yup.string().nullable().optional(),
    telephone: yup.string().nullable().optional(),
    tin: yup.string().nullable().optional(),
  }),

  // Recipient Information
  recipient: yup.object().shape({
    name: yup.string().nullable().optional(),
    streetAddress: yup.string().nullable().optional(),
    cityStateZipCountry: yup.string().nullable().optional(),
    tin: yup.string().nullable().optional(),
    accountNumber: yup.string().nullable().optional(),
  }),

  // Income & Payment Boxes
  box1Rents: yup.string().nullable().optional(),
  box2Royalties: yup.string().nullable().optional(),
  box3OtherIncome: yup.string().nullable().optional(),
  box4FederalIncomeTaxWithheld: yup.string().nullable().optional(),
  box5FishingBoatProceeds: yup.string().nullable().optional(),
  box6MedicalAndHealthCarePayments: yup.string().nullable().optional(),
  box7DirectSalesCheckbox: yup.string().nullable().optional(), // Checkbox: "Yes" or "true" to check
  box7DirectSalesAmount: yup.string().nullable().optional(), // Dollar amount for box 7
  box8SubstitutePayments: yup.string().nullable().optional(),
  box9CropInsuranceProceeds: yup.string().nullable().optional(),
  box10GrossProceedsPaidToAttorney: yup.string().nullable().optional(),
  box11FishPurchasedForResale: yup.string().nullable().optional(),
  box12Section409ADeferrals: yup.string().nullable().optional(),
  box13FatcaFilingRequirement: yup.string().nullable().optional(), // Checkbox: "Yes" or "true" to check
  box14Reserved: yup.string().nullable().optional(), // Reserved for future use
  box15NonqualifiedDeferredCompensation: yup.string().nullable().optional(),

  // State-Related Rows (2-row array support)
  stateRows: yup.array().of(stateRowSchema).max(2).nullable().optional(),
})

export type SlMunson1099FormFileData = yup.InferType<typeof slMunson1099FormFileDataSchema>
type RequiredFileData = MakeRequiredAndNullable<SlMunson1099FormFileData>

// File mappings would be defined here when integrating with forms
export const slMunson1099FormFileMappings: RequiredFileData = {
  taxYear: 'a1b2c3d4-e5f6-4789-a012-b3c4d5e6f789',
  copyType: 'b2c3d4e5-f6a7-4890-b123-c4d5e6f7a890',
  formVariant: 'c3d4e5f6-a7b8-4901-c234-d5e6f7a8b901',
  formRevision: 'c4d5e6f7-a8b9-4902-c345-d6e7f8a9b012',
  corrected: 'd4e5f6a7-b8c9-4012-d345-e6f7a8b9c012',
  secondTinNotification: 'e5f6a7b8-c9d0-4123-e456-f7a8b9c0d123',
  payer: {
    name: 'f6a7b8c9-d0e1-4234-f567-a8b9c0d1e234',
    streetAddress: 'a7b8c9d0-e1f2-4345-a678-b9c0d1e2f345',
    cityStateZipCountry: 'b8c9d0e1-f2a3-4456-b789-c0d1e2f3a456',
    telephone: 'c9d0e1f2-a3b4-4567-c890-d1e2f3a4b567',
    tin: 'd0e1f2a3-b4c5-4678-d901-e2f3a4b5c678',
  },
  recipient: {
    name: 'e1f2a3b4-c5d6-4789-e012-f3a4b5c6d789',
    streetAddress: 'f2a3b4c5-d6e7-4890-f123-a4b5c6d7e890',
    cityStateZipCountry: 'a3b4c5d6-e7f8-4901-a234-b5c6d7e8f901',
    tin: 'b4c5d6e7-f8a9-4012-b345-c6d7e8f9a012',
    accountNumber: 'c5d6e7f8-a9b0-4123-c456-d7e8f9a0b123',
  },
  box1Rents: 'd6e7f8a9-b0c1-4234-d567-e8f9a0b1c234',
  box2Royalties: 'e7f8a9b0-c1d2-4345-e678-f9a0b1c2d345',
  box3OtherIncome: 'f8a9b0c1-d2e3-4456-f789-a0b1c2d3e456',
  box4FederalIncomeTaxWithheld: 'a9b0c1d2-e3f4-4567-a890-b1c2d3e4f567',
  box5FishingBoatProceeds: 'b0c1d2e3-f4a5-4678-b901-c2d3e4f5a678',
  box6MedicalAndHealthCarePayments: 'c1d2e3f4-a5b6-4789-c012-d3e4f5a6b789',
  box7DirectSalesCheckbox: 'd2e3f4a5-b6c7-4890-d123-e4f5a6b7c890',
  box7DirectSalesAmount: 'e3f4a5b6-c7d8-4901-e234-f5a6b7c8d901',
  box8SubstitutePayments: 'f4a5b6c7-d8e9-4012-f345-a6b7c8d9e012',
  box9CropInsuranceProceeds: 'a5b6c7d8-e9f0-4123-a456-b7c8d9e0f123',
  box10GrossProceedsPaidToAttorney: 'b6c7d8e9-f0a1-4234-b567-c8d9e0f1a234',
  box11FishPurchasedForResale: 'c7d8e9f0-a1b2-4345-c678-d9e0f1a2b345',
  box12Section409ADeferrals: 'd8e9f0a1-b2c3-4456-d789-e0f1a2b3c456',
  box13FatcaFilingRequirement: 'e9f0a1b2-c3d4-4567-e890-f1a2b3c4d567',
  box14Reserved: 'f0a1b2c3-d4e5-4678-f901-a2b3c4d5e678',
  box15NonqualifiedDeferredCompensation: 'f1a2b3c4-d5e6-4789-f012-a3b4c5d6e789',
  stateRows: [
    {
      stateTaxWithheld: 'a2b3c4d5-e6f7-4890-a123-b4c5d6e7f890',
      statePayerStateNo: 'b3c4d5e6-f7a8-4901-b234-c5d6e7f8a901',
      stateIncome: 'c4d5e6f7-a8b9-4012-c345-d6e7f8a9b012',
    },
  ],
}
